import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { DateRange } from 'react-date-range';

import ClearIcon from '@mui/icons-material/Clear';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { InputAdornment, Popover, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';

import { POLARIS_BLUE } from '../../themes/theme';
import { calcEndDate } from '../../utils/dateFunctions';
import IconError from '../IconError';

import 'react-date-range/dist/styles.css'; // vendor style file
import 'react-date-range/dist/theme/default.css'; // vendor style file
import common from './formElementStyles/commonFormElements.module.css';
import date from './formElementStyles/dateRange.module.css';

const _DATE_FORMAT = 'dd/MM/yyyy';
const aYearFromNow = new Date();
aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1);

const DateRangePicker = ({
    id,
    label,
    mandatory = false,
    onSearch,
    disabled,
    initialStartDate = '',
    initialEndDate = '',
    minStartDate = null,
    maxDaysAfterStart = '',
    error = {}
}) => {
    const [displayCalendar, setDisplayCalendar] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(aYearFromNow);

    useEffect(() => {
        configDateRange(initialStartDate, initialEndDate);
        if (minStartDate) setMinDate(new Date(minStartDate));
    }, [initialStartDate, initialEndDate, minStartDate]);

    useEffect(() => {
        onSearch(inputValue);
    }, [inputValue]);

    // HELPER FNS
    const configDateRange = (startDate, endDate) => {
        let inputValue = '';
        if (startDate) inputValue += format(startDate, _DATE_FORMAT);
        if (endDate) inputValue += ' - ' + format(endDate, _DATE_FORMAT);
        setInputValue(inputValue);
        setFromDate(startDate);
        setToDate(endDate);
        if (maxDaysAfterStart) setMaxDate(new Date(calcEndDate(startDate, maxDaysAfterStart)));
    };

    // EVENT HANDLERS
    const onAdornmentClick = (e) => {
        if (disabled) return;
        setDisplayCalendar(true);
        setAnchorEl(e.currentTarget);
    };

    const onPopoverClose = () => {
        setDisplayCalendar(false);
        setAnchorEl(null);
    };

    const onSelectDateRanges = ({ selection }) => {
        let { startDate, endDate } = selection;
        configDateRange(startDate, endDate);
    };

    return (
        <div className={common.fieldWrapper}>
            <div className={date.dateRangeSearch} data-testid={`${id}_date_range_picker`}>
                <div className={date.inputContainer}>
                    <label htmlFor={id} className={common.formLabel}>
                        <span>
                            {label} {mandatory && <sup>*</sup>}
                        </span>
                    </label>
                    <TextField
                        sx={{ width: 250 }}
                        aria-required={mandatory}
                        placeholder="Select a date range"
                        value={inputValue}
                        disabled={true}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={onAdornmentClick}>
                                        <div
                                            className={`${date.icons} ${disabled ? date.iconsDisabled : ''}`}>
                                            {!inputValue ? (
                                                <DateRangeIcon sx={{ color: POLARIS_BLUE }} />
                                            ) : (
                                                <ClearIcon
                                                    onClick={() => {
                                                        setInputValue('');
                                                        setDisplayCalendar(false);
                                                        setAnchorEl(null);
                                                    }}
                                                    className={date.clear}
                                                />
                                            )}
                                        </div>
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>
                <Popover
                    open={displayCalendar}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                    onClose={onPopoverClose}>
                    <div>
                        <DateRange
                            onChange={onSelectDateRanges}
                            inputRanges={undefined}
                            minDate={minDate}
                            maxDate={maxDate}
                            moveRangeOnFirstSelection={false}
                            ranges={[
                                {
                                    startDate: fromDate,
                                    endDate: toDate,
                                    key: 'selection'
                                }
                            ]}
                            scroll={{ enabled: false }}
                            showDateDisplay={false}
                            shownDate={new Date()}
                            showMonthAndYearPickers={true}
                            staticRanges={undefined}
                        />
                    </div>
                </Popover>
            </div>
            {error?.error && (
                <div className={date.dateRangeError}>
                    <IconError text={error} />
                </div>
            )}
        </div>
    );
};

DateRangePicker.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    mandatory: PropTypes.bool,
    onSearch: PropTypes.func,
    disabled: PropTypes.bool,
    initialStartDate: PropTypes.string,
    initialEndDate: PropTypes.string,
    minStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    maxDaysAfterStart: PropTypes.number,
    error: PropTypes.object
};

export default DateRangePicker;
